import { z } from "zod";
import { partialLimitsSchema, type BackorderSettings } from "./best-before.js";
import type { Order } from "./order.js";

/**
 * Auftragsregeln je Shop. Gespeichert als versioniertes JSON (order_rule_sets),
 * im Portal per Regel-Editor gepflegt. Neue Bedingungen/Aktionen: hier ergänzen, kein DB-Umbau.
 *
 * Auswertung:
 * 1. Eingebaute Prüfungen (Zahlung, Adresse, unbekannte/gesperrte Artikel) → Sperre
 * 2. Regeln in Reihenfolge; Sperren/Ablehnen sammeln sich, Versandart/Priorität: erste Regel gewinnt
 * 3. Ergebnis enthält alle angewendeten Regeln → Portal zeigt "Warum ist dieser Auftrag gesperrt?"
 */

const condition = z.discriminatedUnion("field", [
  z.object({ field: z.literal("country"), op: z.enum(["in", "not_in"]), value: z.array(z.string().length(2)).min(1) }),
  z.object({ field: z.literal("tag"), op: z.enum(["has", "has_not"]), value: z.string().min(1) }),
  z.object({ field: z.literal("financialStatus"), op: z.enum(["in", "not_in"]), value: z.array(z.string()).min(1) }),
  z.object({ field: z.literal("shippingMethod"), op: z.enum(["equals", "contains"]), value: z.string().min(1) }),
  z.object({ field: z.literal("orderTotal"), op: z.enum(["gte", "lte"]), value: z.number() }),
  z.object({ field: z.literal("sku"), op: z.enum(["contains_any", "contains_none"]), value: z.array(z.string()).min(1) }),
  z.object({ field: z.literal("itemCount"), op: z.enum(["gte", "lte"]), value: z.number().int() }),
  z.object({ field: z.literal("noteContains"), op: z.literal("contains"), value: z.string().min(1) }),
]);

const action = z.discriminatedUnion("type", [
  z.object({ type: z.literal("hold"), reason: z.string().min(1) }),
  z.object({ type: z.literal("reject"), reason: z.string().min(1) }),
  z.object({ type: z.literal("setShippingMethod"), shippingMethodId: z.string().min(1) }),
  z.object({ type: z.literal("setPriority"), priority: z.number().int().min(-5).max(5) }),
  z.object({ type: z.literal("delay"), minutes: z.number().int().min(1).max(10_080) }),
  /**
   * Verhalten bei Fehlbestand für passende Aufträge – geht der Shop- und Kunden-Einstellung vor.
   * partial mit Grenzen (Mindestanteil, Höchstzahl Teile); transfer mit Übergabe an Logiful oder nur ins FFN.
   */
  z.object({
    type: z.literal("backorder"),
    mode: z.enum(["hold", "partial", "transfer"]),
    handover: z.enum(["fulfiller", "merchant"]).default("fulfiller"),
    ...partialLimitsSchema.shape,
  }),
  /**
   * Position hinzufügen (Beilage, Flyer, Probe, Geschenk).
   * Menge: fixed = factor Stück · perItem = factor je Artikel im Auftrag (optional nur countSkus) · perValue = factor je stepEur Warenwert.
   * Abgerundet, höchstens maxQuantity. required = Pflicht: ohne verfügbaren Bestand wird der Auftrag zurückgehalten ("Beilage out of stock").
   */
  z.object({
    type: z.literal("addLine"),
    sku: z.string().trim().min(1),
    mode: z.enum(["fixed", "perItem", "perValue"]).default("fixed"),
    factor: z.number().positive().max(1000).default(1),
    stepEur: z.number().positive().optional(),
    countSkus: z.array(z.string()).default([]),
    maxQuantity: z.number().int().positive().optional(),
    required: z.boolean().default(false),
  }),
]);
export type OrderAction = z.infer<typeof action>;
type AddLineAction = Extract<OrderAction, { type: "addLine" }>;

/** Menge einer Beilage für diesen Auftrag (0 = keine) */
export function addLineQuantity(a: AddLineAction, o: Order): number {
  const counted = o.lines.filter((l) => !a.countSkus.length || a.countSkus.includes(l.sku)).reduce((s, l) => s + l.quantity, 0);
  const raw = a.mode === "fixed" ? a.factor : a.mode === "perItem" ? counted * a.factor : Math.floor((o.total?.amountMinor ?? 0) / 100 / (a.stepEur ?? 1)) * a.factor;
  const q = Math.floor(raw + 1e-9);
  return Math.max(0, a.maxQuantity ? Math.min(q, a.maxQuantity) : q);
}

/** alle SKUs, die Regeln als Beilage hinzufügen können (für die Bestandsabfrage vorab) */
export const insertSkusOf = (rs: OrderRuleSet) => [...new Set(rs.rules.flatMap((r) => r.then.flatMap((a) => (a.type === "addLine" ? [a.sku] : []))))];

export const orderRuleSetSchema = z.object({
  /** Standard-Stornofenster vor Übergabe ans Lager */
  delayMinutes: z.number().int().min(0).max(10_080).default(0),
  /** Nur bezahlte Aufträge übergeben (sonst Sperre) */
  requirePaid: z.boolean().default(true),
  /** Standard-Versandart, wenn keine Regel greift */
  defaultShippingMethodId: z.string().optional(),
  rules: z
    .array(
      z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        enabled: z.boolean().default(true),
        /** alle Bedingungen müssen zutreffen (UND); leere Liste = immer */
        when: z.array(condition).default([]),
        then: z.array(action).min(1),
      }),
    )
    .default([]),
});

export type OrderRuleSet = z.infer<typeof orderRuleSetSchema>;
export type OrderCondition = z.infer<typeof condition>;

export interface OrderRuleContext {
  now: Date;
  /** SKUs, die im System nicht existieren */
  unknownSkus: string[];
  /** SKUs, deren Stammdaten die Übertragung blockieren */
  blockedSkus: string[];
  /** Beilagen: verfügbarer Bestand je SKU; fehlt die SKU = im Portal unbekannt */
  insertStock?: Record<string, number>;
}

export interface AddedLine { sku: string; quantity: number; required: boolean; ruleId: string; ruleName: string }

export interface OrderRuleDecision {
  decision: "release" | "hold" | "reject";
  holdReasons: string[];
  rejectReason?: string;
  shippingMethodId?: string;
  /** Versandart stammt nur aus der Standard-Versandart (keine Regel) – Versandarten-Zuordnung des Shops hat dann Vorrang */
  shippingMethodFromDefault?: boolean;
  priority: number;
  releaseAt: Date;
  appliedRules: { id: string; name: string }[];
  /** von Regeln hinzugefügte Positionen (Beilagen) */
  addLines: AddedLine[];
  /** optionale Beilagen, die mangels Bestand entfallen sind */
  skippedLines: (AddedLine & { reason: string })[];
  /** Verhalten bei Fehlbestand aus einer Regel (erste passende gewinnt) – sonst Shop/Kunde */
  backorder?: BackorderSettings & { ruleName: string };
}

function matches(c: OrderCondition, o: Order): boolean {
  switch (c.field) {
    case "country":
      return c.value.includes(o.shippingAddress.country) === (c.op === "in");
    case "tag": {
      const has = o.tags.some((t) => t.toLowerCase() === c.value.toLowerCase());
      return c.op === "has" ? has : !has;
    }
    case "financialStatus":
      return c.value.includes(o.financialStatus) === (c.op === "in");
    case "shippingMethod": {
      const m = (o.channelShippingMethod ?? "").toLowerCase();
      return c.op === "equals" ? m === c.value.toLowerCase() : m.includes(c.value.toLowerCase());
    }
    case "orderTotal": {
      const total = (o.total?.amountMinor ?? 0) / 100;
      return c.op === "gte" ? total >= c.value : total <= c.value;
    }
    case "sku": {
      const any = o.lines.some((l) => c.value.includes(l.sku));
      return c.op === "contains_any" ? any : !any;
    }
    case "itemCount": {
      const n = o.lines.reduce((s, l) => s + l.quantity, 0);
      return c.op === "gte" ? n >= c.value : n <= c.value;
    }
    case "noteContains":
      return (o.note ?? "").toLowerCase().includes(c.value.toLowerCase());
  }
}

export function evaluateOrderRules(order: Order, ruleSet: OrderRuleSet, ctx: OrderRuleContext): OrderRuleDecision {
  const holdReasons: string[] = [];
  let rejectReason: string | undefined;
  let shippingMethodId: string | undefined;
  let priority: number | undefined;
  let delay = ruleSet.delayMinutes;
  let backorder: OrderRuleDecision["backorder"];
  const appliedRules: { id: string; name: string }[] = [];
  const addLines: AddedLine[] = [];
  const skippedLines: (AddedLine & { reason: string })[] = [];

  // 1. Eingebaute Prüfungen
  if (ruleSet.requirePaid && order.financialStatus !== "paid") holdReasons.push("Zahlung nicht abgeschlossen");
  const a = order.shippingAddress;
  if (!a.street || !a.zip || !a.city || !a.country || !(a.lastName || a.company)) holdReasons.push("Lieferadresse unvollständig");
  if (order.lines.length === 0) holdReasons.push("Keine offenen Positionen");
  if (ctx.unknownSkus.length) holdReasons.push(`Unbekannte Artikel: ${ctx.unknownSkus.join(", ")}`);
  if (ctx.blockedSkus.length) holdReasons.push(`Stammdaten unvollständig: ${ctx.blockedSkus.join(", ")}`);

  // 2. Kundenregeln
  for (const rule of ruleSet.rules) {
    if (!rule.enabled || !rule.when.every((c) => matches(c, order))) continue;
    appliedRules.push({ id: rule.id, name: rule.name });
    for (const act of rule.then) {
      if (act.type === "hold") holdReasons.push(act.reason);
      else if (act.type === "reject") rejectReason ??= act.reason;
      else if (act.type === "setShippingMethod") shippingMethodId ??= act.shippingMethodId;
      else if (act.type === "setPriority") priority ??= act.priority;
      else if (act.type === "delay") delay = Math.max(delay, act.minutes);
      else if (act.type === "backorder") backorder ??= { mode: act.mode, handover: act.handover, minSharePercent: act.minSharePercent ?? null, maxParts: act.maxParts ?? null, ruleName: rule.name };
      else if (act.type === "addLine") {
        const quantity = addLineQuantity(act, order);
        if (quantity > 0) addLines.push({ sku: act.sku, quantity, required: act.required, ruleId: rule.id, ruleName: rule.name });
      }
    }
  }

  // Beilagen gegen Bestand prüfen (je SKU summiert, Pflicht-Beilagen zuerst bedient)
  const stock = { ...(ctx.insertStock ?? {}) };
  const accepted: AddedLine[] = [];
  for (const l of [...addLines].sort((a, b) => Number(b.required) - Number(a.required))) {
    const available = stock[l.sku];
    if (available !== undefined && available >= l.quantity) { stock[l.sku] = available - l.quantity; accepted.push(l); continue; }
    const reason = available === undefined ? "Artikel im Portal unbekannt" : `verfügbar ${Math.max(0, available)}, benötigt ${l.quantity}`;
    if (l.required) { holdReasons.push(`Beilage out of stock: ${l.sku} (${reason})`); accepted.push(l); }
    else skippedLines.push({ ...l, reason });
  }

  const placed = new Date(order.placedAt);
  const releaseAt = new Date(Math.max(ctx.now.getTime(), placed.getTime() + delay * 60_000));
  const resolvedShipping = shippingMethodId ?? ruleSet.defaultShippingMethodId;

  return {
    decision: rejectReason ? "reject" : holdReasons.length ? "hold" : "release",
    holdReasons,
    ...(rejectReason ? { rejectReason } : {}),
    ...(resolvedShipping ? { shippingMethodId: resolvedShipping } : {}),
    ...(!shippingMethodId && resolvedShipping ? { shippingMethodFromDefault: true } : {}),
    // Regel gewinnt nur, wenn sie eine Priorität setzt – sonst bleibt der gespeicherte Wert (z. B. manuelles Express)
    priority: priority ?? order.priority ?? 0,
    releaseAt,
    appliedRules,
    addLines: accepted,
    skippedLines,
    ...(backorder ? { backorder } : {}),
  };
}

export const DEFAULT_ORDER_RULE_SET: OrderRuleSet = orderRuleSetSchema.parse({});
